import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col, Badge } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";

// (2026-08) Seguimiento integral de producción: por orden de producción y corte,
// lo requerido, lo cortado, a qué proceso/maquilero salió y cuánto ha regresado.
function SeguimientoProduccion() {
  const { user } = useContext(AuthContext);
  const URL_ORDENES_CORTE = process.env.REACT_APP_URL_ORDENES_CORTE;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(inicio).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );

  const [renglones, setRenglones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedCortado, setSelectedCortado] = useState("");
  // (2026-09) buscador libre sobre cualquier dato del renglón
  const [search, setSearch] = useState("");
  const [cargando, setCargando] = useState(false);

  // (2026-09) cliente con el subcliente entre paréntesis, como en ListadoPedidos
  // (2026-10) y la subárea de la orden, también entre paréntesis (ListadoOrdenesCorte)
  const clienteConSub = (r) => {
    const conDato = (v) => {
      const t = String(v ?? "").trim();
      return t && t.toUpperCase() !== "NA" ? t : "";
    };
    const sub = conDato(r?.subCliente);
    const subArea = conDato(r?.subArea);
    return [String(r?.cliente || "").trim(), sub && `(${sub})`, subArea && `(${subArea})`]
      .filter(Boolean)
      .join(" ");
  };

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  const arr = (d) => (Array.isArray(d) ? d : []);
  const num = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
  const pct = (parte, total) =>
    Number(total || 0) > 0 ? (Number(parte || 0) / Number(total)) * 100 : 0;

  // (2026-09) número grande y en negritas en los recuadros del resumen
  const estiloKpi = {
    fontSize: "1.7rem",
    fontWeight: 700,
    lineHeight: 1.15,
    margin: "2px 0",
  };

  useEffect(() => {
    axios
      .get(URL_CLIENTES, { headers })
      .then((r) => setClientes(arr(r.data)))
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    setCargando(true);
    axios
      .get(
        `${URL_ORDENES_CORTE}Seguimiento/${selectedFechaInicio}/${selectedFechaFin}`,
        { headers }
      )
      .then((r) => {
        setRenglones(arr(r.data));
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setCargando(false);
        Swal.fire("Error", "No se pudo cargar el seguimiento de producción", "error");
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  const filtrados = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    // todo lo que se ve en el renglón, en un solo texto para buscar
    const textoDe = (r) =>
      [
        r.op,
        clienteConSub(r),
        r.modelo,
        r.articulo,
        r.cantidadOP,
        r.cortado === "Si" ? "si" : "no",
        r.noCorte ? `#${r.noCorte}` : "",
        r.cantidadCortada,
        r.tipoProceso,
        r.maquilero,
        r.salioProceso,
        r.cantidadEntregada,
        r.fecha,
      ]
        .map((v) => String(v ?? ""))
        .join(" ")
        .toLowerCase();

    return renglones.filter(
      (r) =>
        (!selectedCliente || r.cliente === selectedCliente) &&
        (!selectedCortado || r.cortado === selectedCortado) &&
        (!q || textoDe(r).includes(q))
    );
  }, [renglones, selectedCliente, selectedCortado, search]);

  const tot = useMemo(() => {
    const t = { requeridas: 0, cortadas: 0, salieron: 0, entregadas: 0, pendientes: 0 };
    // (2026-10) un renglón por proceso: lo de la OP (requerido, cortado) se
    // repite en cada uno y se cuenta sólo una vez por OP + modelo
    const vistos = new Set();
    filtrados.forEach((r) => {
      t.salieron += Number(r.salioProceso || 0);
      t.entregadas += Number(r.cantidadEntregada || 0);
      const grupo = r.grupo || r._id;
      if (vistos.has(grupo)) return;
      vistos.add(grupo);
      const requerida = Number(r.cantidadOP || 0);
      const cortada = Number(r.cantidadCortada || 0);
      t.requeridas += requerida;
      t.cortadas += cortada;
      // (2026-09) Lo pendiente sale renglón por renglón: si la orden ya está
      // marcada como cortada no falta nada, aunque no tenga corte capturado
      // (p. ej. las muestras de 1 pieza que se marcan a mano).
      t.pendientes +=
        r.cortado === "Si" ? 0 : Math.max(0, requerida - cortada);
    });
    return t;
  }, [filtrados]);

  // Clientes que aparecen en el periodo (para el filtro)
  const clientesEnReporte = useMemo(
    () => [...new Set(renglones.map((r) => r.cliente).filter((x) => x))].sort(),
    [renglones]
  );

  // ---------------- PDF ----------------
  const PDFReporte = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay producción en el periodo/filtros", "warning");
    }

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const ML = 8;
    const usableW = pageW - ML * 2;

    const azulMarino = [23, 37, 64];
    const azul = [46, 117, 182];
    const verde = [22, 101, 52];
    const verdeClaro = [216, 240, 224];
    const naranja = [180, 100, 20];
    const naranjaClaro = [255, 244, 214];
    const gris = [110, 120, 135];
    const bordeGris = [210, 216, 224];

    // ---- Banda de título
    doc.setFillColor(...azulMarino);
    doc.rect(0, 0, pageW, 15, "F");
    doc.setFillColor(...azul);
    doc.rect(0, 15, pageW, 1.2, "F");
    doc.setTextColor(255).setFont(undefined, "bold").setFontSize(14);
    doc.text("REPORTE INTEGRAL DE SEGUIMIENTO DE PRODUCCIÓN", ML, 7.5);
    doc.setFont(undefined, "normal").setFontSize(7.5);
    doc.text(
      `DETALLE OPERATIVO DE PEDIDOS, CORTE, MAQUILA Y MÉTRICAS FINALES DE AVANCE  ·  ${moment(
        selectedFechaInicio
      ).format("DD/MM/YYYY")} - ${moment(selectedFechaFin).format("DD/MM/YYYY")}`,
      ML,
      12.5
    );
    doc.setTextColor(0);

    doc.autoTable({
      startY: 20,
      margin: { left: ML, right: ML, top: 12, bottom: 12 },
      tableWidth: usableW,
      theme: "grid",
      styles: {
        fontSize: 7.5,
        cellPadding: 1.4,
        valign: "middle",
        lineColor: bordeGris,
        lineWidth: 0.15,
        textColor: 40,
      },
      headStyles: {
        fillColor: azulMarino,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        // chico para que "SALIO A PROCESO" / "CANT. ENTREGADA" no se partan feo
        fontSize: 6.5,
        cellPadding: 1,
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      head: [[
        "# OP", "CLIENTE", "MODELO", "Modelo", "CANT. OP", "¿CORTADO?",
        "NO. CORTE", "CANT. CORTADA", "TIPO PROCESO", "MAQUILERO",
        "SALIO A PROCESO", "CANT. ENTREGADA",
      ]],
      columnStyles: {
        0: { cellWidth: 18, fontStyle: "bold" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 26 },
        3: { cellWidth: 20, halign: "center" },
        4: { cellWidth: 17, halign: "right", fontStyle: "bold" },
        5: { cellWidth: 18, halign: "center" },
        6: { cellWidth: 18, halign: "center" },
        7: { cellWidth: 20, halign: "right", fontStyle: "bold" },
        8: { cellWidth: 26 },
        9: { cellWidth: 30 },
        10: { cellWidth: 19, halign: "right" },
        11: { cellWidth: 19, halign: "right" },
      },
      body: filtrados.map((r) => [
        String(r.op || "-"),
        clienteConSub(r) || "-",
        r.modelo || "-",
        String(r.articulo || "-"),
        num(r.cantidadOP),
        r.cortado === "Si" ? "SÍ" : "NO",
        r.noCorte ? `#${r.noCorte}` : "-",
        r.cantidadCortada > 0 ? num(r.cantidadCortada) : "0",
        r.tipoProceso || "-",
        r.maquilero || "-",
        num(r.salioProceso),
        num(r.cantidadEntregada),
      ]),
      foot: [[
        { content: "TOTALES ACUMULADOS:", colSpan: 4, styles: { halign: "right" } },
        num(tot.requeridas),
        "-",
        "-",
        num(tot.cortadas),
        { content: "SUMATORIAS OPERATIVAS:", colSpan: 2, styles: { halign: "right" } },
        num(tot.salieron),
        num(tot.entregadas),
      ]],
      footStyles: {
        fillColor: [226, 233, 241],
        textColor: azulMarino,
        fontStyle: "bold",
        halign: "right",
      },
      didParseCell: (h) => {
        if (h.section !== "body") return;
        // (2026-10) otro proceso de la misma OP + modelo: datos de OP en gris
        const r = filtrados[h.row.index];
        const ant = filtrados[h.row.index - 1];
        if (h.column.index <= 7 && r?.grupo && ant?.grupo === r.grupo) {
          h.cell.styles.textColor = [170, 176, 186];
          if (h.column.index === 5) return;
        }
        if (h.column.index === 5) {
          const si = String(h.cell.raw) === "SÍ";
          h.cell.styles.fillColor = si ? verdeClaro : naranjaClaro;
          h.cell.styles.textColor = si ? verde : naranja;
          h.cell.styles.fontStyle = "bold";
        }
      },
    });

    // ---- Resumen global
    let y = (doc.lastAutoTable?.finalY || 20) + 6;
    const altoResumen = 26;
    if (y + altoResumen > pageH - 12) {
      doc.addPage();
      y = 16;
    }

    doc.setFillColor(...azulMarino);
    doc.rect(ML, y, usableW, 7, "F");
    doc.setFont(undefined, "bold").setFontSize(9).setTextColor(255);
    doc.text(
      "RESUMEN DE PRENDAS PRODUCIDAS, CORTADAS, EN PROCESO Y ENTREGADAS (% AVANCE GLOBAL)",
      ML + 3,
      y + 4.8
    );
    doc.setTextColor(0);
    y += 7;

    const bloques = [
      {
        titulo: "1. PRENDAS REQUERIDAS (OP)",
        etiqueta: "BASE REQUERIDA",
        valor: `${num(tot.requeridas)} Piezas`,
        pie: "100.0% Meta",
        color: azul,
      },
      {
        titulo: "2. PRENDAS CORTADAS",
        etiqueta: "AVANCE DE CORTE",
        valor: `${num(tot.cortadas)} Piezas`,
        pie: `${pct(tot.cortadas, tot.requeridas).toFixed(1)}% Cortado`,
        color: azul,
      },
      {
        titulo: "3. PENDIENTES DE CORTE",
        etiqueta: "POR CORTAR",
        valor: `${num(tot.pendientes)} Piezas`,
        pie: `${pct(tot.pendientes, tot.requeridas).toFixed(1)}% Pendiente`,
        color: naranja,
      },
      {
        titulo: "4. SALIDAS A PROCESO",
        etiqueta: "EN MAQUILA / PROCESO",
        valor: `${num(tot.salieron)} Piezas`,
        pie: `${pct(tot.salieron, tot.requeridas).toFixed(1)}% vs Requerido`,
        color: azul,
      },
      {
        titulo: "5. PRENDAS ENTREGADAS",
        etiqueta: "CONCLUIDAS Y RECIBIDAS",
        valor: `${num(tot.entregadas)} Piezas`,
        pie: `${pct(tot.entregadas, tot.requeridas).toFixed(1)}% Avance Final`,
        color: verde,
      },
    ];

    const bloqueW = usableW / bloques.length;
    // encabezados de los bloques
    doc.setFillColor(...azulMarino);
    doc.rect(ML, y, usableW, 6, "F");
    bloques.forEach((b, i) => {
      doc.setFont(undefined, "bold").setFontSize(7).setTextColor(255);
      doc.text(b.titulo, ML + bloqueW * i + bloqueW / 2, y + 4, { align: "center" });
      if (i > 0) {
        doc.setDrawColor(255).setLineWidth(0.2);
        doc.line(ML + bloqueW * i, y, ML + bloqueW * i, y + 6 + 20);
      }
    });
    doc.setTextColor(0);

    doc.setDrawColor(...bordeGris).setLineWidth(0.3).setFillColor(252, 253, 255);
    doc.rect(ML, y + 6, usableW, 20, "FD");

    bloques.forEach((b, i) => {
      const cx = ML + bloqueW * i + bloqueW / 2;
      doc.setFont(undefined, "bold").setFontSize(7).setTextColor(...gris);
      doc.text(b.etiqueta, cx, y + 11, { align: "center" });
      doc.setFontSize(12).setTextColor(30);
      doc.text(b.valor, cx, y + 17.5, { align: "center" });
      doc.setFontSize(8).setTextColor(...b.color);
      doc.text(b.pie, cx, y + 23, { align: "center" });
      doc.setTextColor(0);
      if (i > 0) {
        doc.setDrawColor(...bordeGris).setLineWidth(0.2);
        doc.line(ML + bloqueW * i, y + 6, ML + bloqueW * i, y + 26);
      }
    });

    // ---- Pie
    const paginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setFont(undefined, "normal").setFontSize(7).setTextColor(...gris);
      doc.text(
        "LANGIANI ERP - Seguimiento de Producción",
        ML,
        pageH - 5
      );
      doc.text(`Página ${p} de ${paginas}`, pageW - ML, pageH - 5, { align: "right" });
      doc.setTextColor(0);
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  const excel = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay producción en el periodo/filtros", "warning");
    }
    const option = {};
    option.fileName = "SeguimientoProduccion";
    option.datas = [
      {
        sheetData: filtrados.map((r) => ({
          OP: r.op,
          Cliente: clienteConSub(r),
          Modelo: r.modelo,
          Articulo: r.articulo,
          CantidadOP: r.cantidadOP,
          Cortado: r.cortado,
          NoCorte: r.noCorte,
          CantidadCortada: r.cantidadCortada,
          TipoProceso: r.tipoProceso,
          Maquilero: r.maquilero,
          SalioProceso: r.salioProceso,
          Entregada: r.cantidadEntregada,
        })),
        sheetName: "Seguimiento",
        sheetFilter: ["OP","Cliente","Modelo","Articulo","CantidadOP","Cortado","NoCorte","CantidadCortada","TipoProceso","Maquilero","SalioProceso","Entregada"],
        sheetHeader: ["# OP","Cliente","Modelo","Modelo","Cant. OP","¿Cortado?","No. Corte","Cant. Cortada","Tipo Proceso","Maquilero","Salió a Proceso","Cant. Entregada"],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_produccion ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuProduccion" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFReporte}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Seguimiento de Producción</h3>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label>Cliente</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedCliente}
                onChange={(e) => setSelectedCliente(e.target.value)}
              >
                <option value="">Todos</option>
                {clientesEnReporte.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>¿Cortado?</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedCortado}
                onChange={(e) => setSelectedCortado(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Si">Si</option>
                <option value="No">No</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label>Buscar</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="OP, cliente, modelo, corte, maquilero..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
          </Row>

          <br />

          {/* Resumen global */}
          <Row>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">1. PRENDAS REQUERIDAS (OP)</small>
                <div style={estiloKpi}>
                  {num(tot.requeridas)} <span style={{ fontSize: "0.9rem", fontWeight: 500 }}>Pzs</span>
                </div>
                <small style={{ color: "#2E75B6" }}>100.0% Meta</small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">2. PRENDAS CORTADAS</small>
                <div style={estiloKpi}>
                  {num(tot.cortadas)} <span style={{ fontSize: "0.9rem", fontWeight: 500 }}>Pzs</span>
                </div>
                <small style={{ color: "#2E75B6" }}>
                  {pct(tot.cortadas, tot.requeridas).toFixed(1)}% Cortado
                </small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">3. PENDIENTES DE CORTE</small>
                <div style={estiloKpi}>
                  {num(tot.pendientes)} <span style={{ fontSize: "0.9rem", fontWeight: 500 }}>Pzs</span>
                </div>
                <small style={{ color: "#B46414" }}>
                  {pct(tot.pendientes, tot.requeridas).toFixed(1)}% Pendiente
                </small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">4. SALIDAS A PROCESO</small>
                <div style={estiloKpi}>
                  {num(tot.salieron)} <span style={{ fontSize: "0.9rem", fontWeight: 500 }}>Pzs</span>
                </div>
                <small style={{ color: "#2E75B6" }}>
                  {pct(tot.salieron, tot.requeridas).toFixed(1)}% vs Requerido
                </small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">5. PRENDAS ENTREGADAS</small>
                <div style={estiloKpi}>
                  {num(tot.entregadas)} <span style={{ fontSize: "0.9rem", fontWeight: 500 }}>Pzs</span>
                </div>
                <small style={{ color: "#166534" }}>
                  {pct(tot.entregadas, tot.requeridas).toFixed(1)}% Avance Final
                </small>
              </div>
            </Col>
          </Row>

          <br />
          {cargando ? <h5 align="center">Cargando...</h5> : null}

          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th># OP</th>
                <th>Cliente</th>
                <th>Modelo</th>
                <th className="text-center">Modelo</th>
                <th className="text-end">Cant. OP</th>
                <th className="text-center">¿Cortado?</th>
                <th className="text-center">No. Corte</th>
                <th className="text-end">Cant. Cortada</th>
                <th>Tipo Proceso</th>
                <th>Maquilero</th>
                <th className="text-end">Salió a Proceso</th>
                <th className="text-end">Cant. Entregada</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length ? (
                filtrados.map((r, i) => {
                  // (2026-10) otro proceso de la misma OP + modelo: sus datos
                  // de OP van tenues para que se lea como continuación
                  const tenue =
                    i > 0 && r.grupo && filtrados[i - 1].grupo === r.grupo
                      ? { opacity: 0.4 }
                      : undefined;
                  return (
                  <tr key={r._id}>
                    <td className="negrita" style={tenue}>{r.op || "-"}</td>
                    <td style={tenue}>{clienteConSub(r)}</td>
                    <td style={tenue}>{r.modelo}</td>
                    <td className="text-center" style={tenue}>{r.articulo}</td>
                    <td className="text-end negrita" style={tenue}>{num(r.cantidadOP)}</td>
                    <td className="text-center" style={tenue}>
                      <Badge color={r.cortado === "Si" ? "success" : "warning"}>
                        {r.cortado === "Si" ? "SÍ" : "NO"}
                      </Badge>
                    </td>
                    <td className="text-center" style={tenue}>
                      {r.noCorte ? `#${r.noCorte}` : "-"}
                    </td>
                    <td className="text-end negrita" style={tenue}>{num(r.cantidadCortada)}</td>
                    <td>{r.tipoProceso || "-"}</td>
                    <td>{r.maquilero || "-"}</td>
                    <td className="text-end">{num(r.salioProceso)}</td>
                    <td className="text-end">{num(r.cantidadEntregada)}</td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className="text-center text-muted">
                    No hay producción en el periodo seleccionado
                  </td>
                </tr>
              )}
              {filtrados.length ? (
                <tr>
                  <td colSpan="4" className="text-end negrita">
                    TOTALES ACUMULADOS:
                  </td>
                  <td className="text-end negrita">{num(tot.requeridas)}</td>
                  <td colSpan="2"></td>
                  <td className="text-end negrita">{num(tot.cortadas)}</td>
                  <td colSpan="2" className="text-end negrita">
                    SUMATORIAS OPERATIVAS:
                  </td>
                  <td className="text-end negrita">{num(tot.salieron)}</td>
                  <td className="text-end negrita">{num(tot.entregadas)}</td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          <br />
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default SeguimientoProduccion;
